import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getFamBySlug } from "../../../../lib/api";
import { getCurrentUser, type CurrentUser } from "../../../../lib/auth";
import {
	useInviteMember,
	useKickMember,
	useMintClaimLink,
	useUpdateFam,
	useUpdateMemberRole,
	type ClaimLinkMintResponse,
} from "../../../../lib/api/fam-members";
import { MemberRoleSelect } from "../../../../components/MemberRoleSelect";
import { RoleBadge } from "../../../../components/RoleBadge";
import type { FamRole } from "../../../../lib/types";

// /fam/:slug/settings — Fam settings page. PM-only edits, but non-PM
// viewers still see the page in read-only mode (write controls hidden).
//
// Sections (top → bottom):
//   1. Rename / logo URL form
//   2. Members table with role dropdowns + kick button
//   3. Invite by X handle form
//   4. Tier upgrade CTA (links to /upgrade once Stripe ships)
export function FamSettingsRoute() {
	const { slug } = useParams<{ slug: string }>();
	const [user, setUser] = useState<CurrentUser | null>(null);

	useEffect(() => {
		void getCurrentUser().then(setUser);
	}, []);

	const famQuery = useQuery({
		queryKey: ["fam", slug],
		queryFn: () => {
			if (!slug) throw new Error("missing slug");
			return getFamBySlug(slug);
		},
		enabled: !!slug,
	});

	const updateFam = useUpdateFam(slug ?? "");
	const updateRole = useUpdateMemberRole(slug ?? "");
	const invite = useInviteMember(slug ?? "");
	const kick = useKickMember(slug ?? "");
	const mintClaim = useMintClaimLink(slug ?? "");

	const [renameValue, setRenameValue] = useState("");
	const [logoValue, setLogoValue] = useState("");
	const [inviteHandle, setInviteHandle] = useState("");
	const [inviteRole, setInviteRole] = useState<FamRole>("member");
	const [pendingRoleUserId, setPendingRoleUserId] = useState<string | null>(null);
	const [pendingKickUserId, setPendingKickUserId] = useState<string | null>(null);
	// Claim-link state — the role the PM picks before minting + the most
	// recently minted token's URL/expiry (cleared on slug change so we don't
	// leak across Fams).
	const [claimRole, setClaimRole] = useState<FamRole>("trader");
	const [mintedClaim, setMintedClaim] = useState<ClaimLinkMintResponse | null>(null);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (famQuery.data) {
			setRenameValue(famQuery.data.name);
			setLogoValue(famQuery.data.logoUrl ?? "");
		}
	}, [famQuery.data]);

	if (famQuery.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center text-[var(--color-muted)]">
				Loading settings…
			</div>
		);
	}
	if (famQuery.isError || !famQuery.data) {
		return (
			<div className="mx-auto max-w-2xl px-4 py-8 text-center">
				<p className="text-sm text-[var(--color-danger)]">
					Could not load Fam settings.{" "}
					{famQuery.error instanceof Error ? famQuery.error.message : ""}
				</p>
				<Link
					to="/"
					className="mt-4 inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					Back to dashboard
				</Link>
			</div>
		);
	}

	const fam = famQuery.data;
	const currentUserId = user?.id ?? "";
	const callerMembership = fam.members.find((m) => m.userId === currentUserId);
	const isPM = callerMembership?.role === "pm";

	async function onRenameSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!isPM) return;
		await updateFam.mutateAsync({
			name: renameValue.trim(),
			logoUrl: logoValue.trim() || undefined,
		});
	}

	async function onRoleChange(userId: string, role: FamRole) {
		if (!isPM) return;
		setPendingRoleUserId(userId);
		try {
			await updateRole.mutateAsync({ userId, role });
		} finally {
			setPendingRoleUserId(null);
		}
	}

	async function onKick(userId: string) {
		if (!isPM) return;
		if (!window.confirm("Remove this member from the Fam?")) return;
		setPendingKickUserId(userId);
		try {
			await kick.mutateAsync({ userId });
		} finally {
			setPendingKickUserId(null);
		}
	}

	async function onInviteSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!isPM) return;
		const handle = inviteHandle.trim().replace(/^@/, "");
		if (!handle) return;
		try {
			await invite.mutateAsync({ xHandle: handle, role: inviteRole });
			setInviteHandle("");
			setInviteRole("member");
		} catch {
			// Surface error via invite.error below.
		}
	}

	async function onMintClaim() {
		if (!isPM) return;
		setCopied(false);
		try {
			const minted = await mintClaim.mutateAsync({ role: claimRole });
			setMintedClaim(minted);
		} catch {
			// Surface error via mintClaim.error below; mintedClaim stays null.
		}
	}

	async function onCopyClaimUrl() {
		if (!mintedClaim) return;
		try {
			await navigator.clipboard.writeText(mintedClaim.url);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1800);
		} catch {
			// Some browsers (Safari in non-secure contexts) reject clipboard
			// writes silently — fall back to manual selection prompts.
			setCopied(false);
		}
	}

	return (
		<div className="mx-auto max-w-3xl space-y-8 px-4 py-8">
			<header>
				<Link
					to={`/fam/${slug}`}
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Back to {fam.name}
				</Link>
				<h1 className="mt-1 text-2xl font-bold">Fam settings</h1>
				<p className="text-sm text-[var(--color-muted)]">
					{fam.members.length}/{fam.maxMembers} members ·{" "}
					{fam.tier === "paid" ? "Paid" : "Free"} tier
					{!isPM && " · read-only (PM only can edit)"}
				</p>
			</header>

			{/* --- Rename + logo --- */}
			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
				<h2 className="text-sm font-semibold">Details</h2>
				<form onSubmit={onRenameSubmit} className="space-y-3">
					<label className="block text-xs text-[var(--color-muted)]">
						Name
						<input
							type="text"
							value={renameValue}
							disabled={!isPM}
							onChange={(e) => setRenameValue(e.target.value)}
							className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-1.5 text-sm text-[var(--color-fg)] disabled:opacity-60"
						/>
					</label>
					<label className="block text-xs text-[var(--color-muted)]">
						Logo URL
						<input
							type="url"
							value={logoValue}
							disabled={!isPM}
							onChange={(e) => setLogoValue(e.target.value)}
							placeholder="https://…"
							className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-1.5 text-sm text-[var(--color-fg)] disabled:opacity-60"
						/>
					</label>
					{isPM && (
						<button
							type="submit"
							disabled={
								updateFam.isPending || renameValue.trim().length === 0
							}
							className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
						>
							{updateFam.isPending ? "Saving…" : "Save changes"}
						</button>
					)}
					{updateFam.error && (
						<p className="text-xs text-[var(--color-danger)]">
							{updateFam.error.code}: {updateFam.error.message}
						</p>
					)}
				</form>
			</section>

			{/* --- Members table --- */}
			<section className="space-y-3">
				<h2 className="text-sm font-semibold">Members</h2>
				<div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
					<table className="w-full table-fixed">
						<thead>
							<tr className="border-b border-[var(--color-border)] text-left text-xs text-[var(--color-muted)]">
								<th className="px-3 py-2 font-normal">Member</th>
								<th className="px-3 py-2 font-normal">Role</th>
								<th className="px-3 py-2 font-normal text-right">
									Actions
								</th>
							</tr>
						</thead>
						<tbody>
							{fam.members.map((m) => {
								const isSelf = m.userId === currentUserId;
								const isPMRow = m.role === "pm";
								const roleBusy = pendingRoleUserId === m.userId;
								const kickBusy = pendingKickUserId === m.userId;
								return (
									<tr
										key={m.userId}
										className="border-b border-[var(--color-border)] text-sm last:border-b-0"
									>
										<td className="px-3 py-2">
											{m.xHandle
												? `@${m.xHandle}`
												: m.userId.slice(0, 8)}
											{isSelf && (
												<span className="ml-2 text-xs text-[var(--color-muted)]">
													(you)
												</span>
											)}
										</td>
										<td className="px-3 py-2">
											{isPM && !isPMRow ? (
												<MemberRoleSelect
													value={m.role}
													disabled={roleBusy}
													onChange={(role) =>
														void onRoleChange(m.userId, role)
													}
												/>
											) : (
												<RoleBadge role={m.role} />
											)}
										</td>
										<td className="px-3 py-2 text-right">
											{isPM && !isPMRow && !isSelf && (
												<button
													type="button"
													disabled={kickBusy}
													onClick={() => void onKick(m.userId)}
													className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-50"
												>
													{kickBusy ? "Kicking…" : "Kick"}
												</button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
				{updateRole.error && (
					<p className="text-xs text-[var(--color-danger)]">
						{updateRole.error.code}: {updateRole.error.message}
					</p>
				)}
				{kick.error && (
					<p className="text-xs text-[var(--color-danger)]">
						{kick.error.code}: {kick.error.message}
					</p>
				)}
			</section>

			{/* Fantasy Fams enrollment was a per-Fam opt-in; removed in migration 041
			    because Fantasy Fam is now its own ephemeral entity. The new
			    Fantasy Fam create flow lives on /fantasy. */}

			{/* --- Invite by X handle (PM only) --- */}
			{isPM && (
				<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
					<h2 className="text-sm font-semibold">Invite a member</h2>
					<form
						onSubmit={onInviteSubmit}
						className="flex flex-col gap-3 sm:flex-row sm:items-end"
					>
						<label className="flex-1 text-xs text-[var(--color-muted)]">
							X handle
							<input
								type="text"
								value={inviteHandle}
								onChange={(e) => setInviteHandle(e.target.value)}
								placeholder="@username"
								className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-1.5 text-sm text-[var(--color-fg)]"
							/>
						</label>
						<label className="text-xs text-[var(--color-muted)]">
							Role
							<div className="mt-1">
								<MemberRoleSelect
									value={inviteRole}
									onChange={setInviteRole}
									disabled={invite.isPending}
								/>
							</div>
						</label>
						<button
							type="submit"
							disabled={
								invite.isPending || inviteHandle.trim().length === 0
							}
							className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
						>
							{invite.isPending ? "Inviting…" : "Invite"}
						</button>
					</form>
					{invite.error && (
						<p className="text-xs text-[var(--color-danger)]">
							{invite.error.code}: {invite.error.message}
						</p>
					)}
					<p className="text-xs text-[var(--color-muted)]">
						The invitee must have signed in to Hedge Fam at least once so we
						can resolve their X handle to a user id.
					</p>
				</section>
			)}

			{/* --- Share a claim link (PM only) ---
			    Generates a signed claim-link URL the PM can DM / iMessage / email
			    to a friend. The token is HMAC-signed (CLAIM_LINK_SIGNING_KEY) and
			    valid for 7 days; the friend clicks → sees a read-only preview on
			    hedgefam.com/c/<token> → signs in on app.hedgefam.com → gets
			    auto-added with the pre-baked role. */}
			{isPM && (
				<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
					<div className="flex items-start justify-between gap-3">
						<div>
							<h2 className="text-sm font-semibold">Share a claim link</h2>
							<p className="mt-1 text-xs text-[var(--color-muted)]">
								Generate a one-link invite. They land on hedgefam.com,
								sign in, and join automatically with the role you pick
								here.
							</p>
						</div>
					</div>

					<div className="flex flex-col gap-3 sm:flex-row sm:items-end">
						<label className="text-xs text-[var(--color-muted)]">
							Role they'll join as
							<div className="mt-1">
								<MemberRoleSelect
									value={claimRole}
									onChange={setClaimRole}
									disabled={mintClaim.isPending}
								/>
							</div>
						</label>
						<button
							type="button"
							onClick={() => void onMintClaim()}
							disabled={mintClaim.isPending}
							className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
						>
							{mintClaim.isPending
								? "Generating…"
								: mintedClaim
									? "Regenerate link"
									: "Generate invite link"}
						</button>
					</div>

					{mintClaim.error && (
						<p className="text-xs text-[var(--color-danger)]">
							{mintClaim.error.code}: {mintClaim.error.message}
						</p>
					)}

					{mintedClaim && (
						<div className="space-y-2 rounded-md border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/[0.04] p-3">
							<div className="flex items-center gap-2">
								<input
									type="text"
									readOnly
									value={mintedClaim.url}
									onFocus={(e) => e.currentTarget.select()}
									className="flex-1 truncate rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-2 py-1.5 text-xs font-mono text-[var(--color-fg)]"
									aria-label="Claim link URL"
								/>
								<button
									type="button"
									onClick={() => void onCopyClaimUrl()}
									className="rounded-md border border-[var(--color-border)] px-2 py-1.5 text-xs hover:border-[var(--color-accent)]"
								>
									{copied ? "Copied" : "Copy"}
								</button>
							</div>
							<p className="text-xs text-[var(--color-muted)]">
								Role:{" "}
								<strong className="text-[var(--color-fg)]">
									{mintedClaim.role}
								</strong>{" "}
								· Expires{" "}
								{new Date(mintedClaim.expiresAt * 1000).toLocaleString()}
							</p>
							<p className="text-xs text-[var(--color-muted)]">
								Anyone with this link can join — keep it private. Generate
								a new one anytime to invalidate older shares (they expire
								on their own after the timestamp above).
							</p>
						</div>
					)}
				</section>
			)}

			{/* --- Upgrade CTA --- */}
			{isPM && fam.tier === "free" && (
				<section className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elev)] p-4">
					<h2 className="text-sm font-semibold">Upgrade your Fam</h2>
					<p className="mt-1 text-xs text-[var(--color-muted)]">
						Paid tier unlocks 5 Fams per PM + 25 members per Fam.
					</p>
					<Link
						to="/upgrade"
						className="mt-3 inline-block rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:bg-emerald-400"
					>
						Upgrade for 5 Fams + 25 members
					</Link>
				</section>
			)}
		</div>
	);
}
